// Bill.ts
import { v4 as uuidv4 } from "uuid";
import { DateUtil } from "../utils/DateUtil";
import { LocalDate, ChronoUnit } from "@js-joda/core";
import { Currency } from "../utils/Currency";
import { AmortizationEntry } from "./Amortization/AmortizationEntry";
import { BillPaymentDetail } from "./Bill/BillPaymentDetail";
export class Bill {
    id;
    period;
    dueDate;
    openDate;
    _principalDue;
    _originalPrincipalDue;
    _interestDue;
    _originalInterestDue;
    _feesDue;
    _originalFeesDue;
    _totalDue;
    _originalTotalDue;
    isDSIBill;
    _versionId = uuidv4();
    _dateChanged = LocalDate.now();
    initialized = false;
    bills;
    amortizationEntry;
    // New property to track deposit IDs used for payment
    paymentMetadata;
    paymentDetails = [];
    dateFullySatisfied;
    daysLate;
    daysEarly;
    constructor(params) {
        this.id = params.id;
        this.period = params.period;
        this.dueDate = DateUtil.normalizeDate(params.dueDate);
        this.openDate = DateUtil.normalizeDate(params.openDate);
        this.principalDue = params.principalDue;
        this.originalPrincipalDue = params.originalPrincipalDue || params.principalDue;
        this.interestDue = params.interestDue;
        this.originalInterestDue = params.originalInterestDue || params.interestDue;
        this.feesDue = params.feesDue;
        this.originalFeesDue = params.originalFeesDue || params.feesDue;
        // this.isPaid = params.isPaid;
        // this.isOpen = params.isOpen;
        // this.isDue = params.isDue;
        // check if amortizationEntry is an instance of AmortizationEntry
        // if not, create a new AmortizationEntry object from the value
        if (params.amortizationEntry instanceof AmortizationEntry) {
            this.amortizationEntry = params.amortizationEntry;
        }
        else {
            this.amortizationEntry = new AmortizationEntry(params.amortizationEntry);
        }
        this.isDSIBill = params.isDSIBill || false;
        if (params.paymentMetadata) {
            this.paymentMetadata = params.paymentMetadata;
        }
        if (params.paymentDetails) {
            this.paymentDetails = params.paymentDetails.map((detail) => {
                return new BillPaymentDetail(detail);
            });
        }
        if (params.dateFullySatisfied) {
            this.dateFullySatisfied = DateUtil.normalizeDate(params.dateFullySatisfied);
        }
        this.daysLate = params.daysLate ?? 0;
        this.daysEarly = params.daysEarly ?? 0;
        if (params.bills) {
            this.bills = params.bills;
        }
        this.initialized = true;
        this.versionChanged();
    }
    get versionId() {
        return this._versionId;
    }
    get dateChanged() {
        return this._dateChanged;
    }
    getDaysPastDue(date) {
        const currentDate = DateUtil.normalizeDate(date);
        if (this.isPaid()) {
            return 0;
        }
        if (!this.isOpen(currentDate)) {
            return 0;
        }
        if (!this.isDue(currentDate)) {
            return 0;
        }
        // calculate days between due date and current date
        const days = ChronoUnit.DAYS.between(this.dueDate, currentDate);
        return days > 0 ? days : 0;
    }
    isOpen(date) {
        const currentDate = DateUtil.normalizeDate(date);
        return this.openDate.isEqual(currentDate) || this.openDate.isBefore(currentDate);
    }
    isDue(date) {
        const currentDate = DateUtil.normalizeDate(date);
        return this.dueDate.isEqual(currentDate) || this.dueDate.isBefore(currentDate);
    }
    isPaid() {
        return this.principalDue.isZero() && this.interestDue.isZero() && this.feesDue.isZero();
    }
    isPastDue(date) {
        const currentDate = DateUtil.normalizeDate(date);
        // unpaid and due date is before current date
        return !this.isPaid() && currentDate.isAfter(this.dueDate);
    }
    versionChanged() {
        if (this.initialized === true) {
            // Use the parent’s currentDate if available.
            // If for some reason there’s no parent (this.bills), you can fallback to LocalDate.now().
            this._dateChanged = this.bills ? this.bills.currentDate : LocalDate.now();
            this._versionId = uuidv4();
            if (this.bills) {
                this.bills.versionChanged();
            }
        }
    }
    get originalPrincipalDue() {
        return this._originalPrincipalDue;
    }
    get originalInterestDue() {
        return this._originalInterestDue;
    }
    get originalFeesDue() {
        return this._originalFeesDue;
    }
    get originalTotalDue() {
        return this._originalPrincipalDue.add(this._originalInterestDue).add(this._originalFeesDue);
    }
    set originalPrincipalDue(value) {
        this._originalPrincipalDue = Currency.of(value);
        this.versionChanged();
    }
    set originalInterestDue(value) {
        this._originalInterestDue = Currency.of(value);
        this.versionChanged();
    }
    set originalFeesDue(value) {
        this._originalFeesDue = Currency.of(value);
        this.versionChanged();
    }
    get principalDue() {
        return this._principalDue;
    }
    set principalDue(value) {
        this._principalDue = Currency.of(value);
        this.versionChanged();
    }
    get interestDue() {
        return this._interestDue;
    }
    set interestDue(value) {
        this._interestDue = Currency.of(value);
        this.versionChanged();
    }
    get feesDue() {
        return this._feesDue;
    }
    set feesDue(value) {
        this._feesDue = Currency.of(value);
        this.versionChanged();
    }
    get totalDue() {
        return this.principalDue.add(this.interestDue).add(this.feesDue);
    }
    reducePrincipalDueBy(amount) {
        this.principalDue = this.principalDue.subtract(amount);
    }
    reduceInterestDueBy(amount) {
        this.interestDue = this.interestDue.subtract(amount);
    }
    reduceFeesDueBy(amount) {
        this.feesDue = this.feesDue.subtract(amount);
    }
    increasePrincipalDueBy(amount) {
        this.principalDue = this.principalDue.add(amount);
    }
    increaseInterestDueBy(amount) {
        this.interestDue = this.interestDue.add(amount);
    }
    increaseFeesDueBy(amount) {
        this.feesDue = this.feesDue.add(amount);
    }
    get jsOpenDate() {
        return DateUtil.normalizeDateToJsDate(this.openDate);
    }
    get jsDueDate() {
        return DateUtil.normalizeDateToJsDate(this.dueDate);
    }
    get jsTotalDue() {
        return this.totalDue.toNumber();
    }
    get jsPrincipalDue() {
        return this.principalDue.toNumber();
    }
    get jsInterestDue() {
        return this.interestDue.toNumber();
    }
    get jsFeesDue() {
        return this.feesDue.toNumber();
    }
    get jsDateFullySatisfied() {
        if (!this.dateFullySatisfied) {
            return undefined;
        }
        return DateUtil.normalizeDateToJsDate(this.dateFullySatisfied);
    }
    // 1) Simple numeric getters for the original amounts
    get jsOriginalPrincipalDue() {
        return this.originalPrincipalDue.toNumber();
    }
    get jsOriginalInterestDue() {
        return this.originalInterestDue.toNumber();
    }
    get jsOriginalFeesDue() {
        return this.originalFeesDue.toNumber();
    }
    get jsOriginalTotalDue() {
        return this.originalTotalDue.toNumber();
    }
    // 2) Sum of allocated amounts from paymentDetails
    get jsAllocatedPrincipalSum() {
        return this.paymentDetails.reduce((sum, detail) => sum + detail.jsAllocatedPrincipal, 0);
    }
    get allocatedPrincipalSum() {
        return this.paymentDetails.reduce((sum, detail) => sum.add(detail.allocatedPrincipal), Currency.zero);
    }
    get jsAllocatedInterestSum() {
        return this.paymentDetails.reduce((sum, detail) => sum + detail.jsAllocatedInterest, 0);
    }
    get allocatedInterestSum() {
        return this.paymentDetails.reduce((sum, detail) => sum.add(detail.allocatedInterest), Currency.zero);
    }
    get jsAllocatedFeesSum() {
        return this.paymentDetails.reduce((sum, detail) => sum + detail.jsAllocatedFees, 0);
    }
    get allocatedFeesSum() {
        return this.paymentDetails.reduce((sum, detail) => sum.add(detail.allocatedFees), Currency.zero);
    }
    get jsAllocatedTotalSum() {
        return this.paymentDetails.reduce((sum, detail) => sum + detail.jsAllocatedTotal, 0);
    }
    get allocatedTotalSum() {
        return this.paymentDetails.reduce((sum, detail) => sum.add(detail.allocatedTotal), Currency.zero);
    }
    // 3) Remaining = Original - Sum(allocated)
    get jsRemainingPrincipal() {
        return this.jsOriginalPrincipalDue - this.jsAllocatedPrincipalSum;
    }
    get remainingPrincipal() {
        return this.originalPrincipalDue.subtract(this.allocatedPrincipalSum);
    }
    get jsRemainingInterest() {
        return this.jsOriginalInterestDue - this.jsAllocatedInterestSum;
    }
    get remainingInterest() {
        return this.originalInterestDue.subtract(this.allocatedInterestSum);
    }
    get jsRemainingFees() {
        return this.jsOriginalFeesDue - this.jsAllocatedFeesSum;
    }
    get remainingFees() {
        return this.originalFeesDue.subtract(this.allocatedFeesSum);
    }
    get jsRemainingTotal() {
        return this.jsOriginalTotalDue - this.jsAllocatedTotalSum;
    }
    get remainingTotal() {
        return this.originalTotalDue.subtract(this.allocatedTotalSum);
    }
    get summary() {
        return {
            totalDue: this.totalDue,
            principalDue: this.principalDue,
            interestDue: this.interestDue,
            feesDue: this.feesDue,
            remainingTotal: this.remainingTotal,
            remainingFees: this.remainingFees,
            remainingPrincipal: this.remainingPrincipal,
            remainingInterest: this.remainingInterest,
            allocatedTotalSum: this.allocatedTotalSum,
            allocatedFeesSum: this.allocatedFeesSum,
            allocatedPrincipalSum: this.allocatedPrincipalSum,
            allocatedInterestSum: this.allocatedInterestSum,
        };
    }
    toJSON() {
        return this.json;
    }
    get json() {
        return {
            id: this.id,
            period: this.period,
            dueDate: this.jsDueDate,
            openDate: this.jsOpenDate,
            principalDue: this.jsPrincipalDue,
            interestDue: this.jsInterestDue,
            feesDue: this.jsFeesDue,
            originalPrincipalDue: this.jsOriginalPrincipalDue,
            originalInterestDue: this.jsOriginalInterestDue,
            originalFeesDue: this.jsOriginalFeesDue,
            isDSIBill: this.isDSIBill,
            amortizationEntry: this.amortizationEntry.toJSON(),
            paymentMetadata: this.paymentMetadata,
            paymentDetails: this.paymentDetails.map((detail) => detail.json),
            dateFullySatisfied: this.jsDateFullySatisfied,
            daysLate: this.daysLate,
            daysEarly: this.daysEarly,
        };
    }
}
